import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface ExperienceRow {
  slug: string;
  title: string;
  intro: string;
  hero_media_asset_id: string | null;
  match_rules: Record<string, unknown>;
  status: string;
  order_index: number;
}

/**
 * All public experiences, ordered by order_index.
 */
export function usePublishedExperiences() {
  return useQuery<ExperienceRow[]>({
    queryKey: ['experiences', 'public'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('experiences')
        .select('slug, title, intro, hero_media_asset_id, match_rules, status, order_index')
        .eq('status', 'public')
        .order('order_index', { ascending: true })
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as ExperienceRow[];
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Single experience by slug (public only for public pages).
 */
export function useExperienceBySlug(slug: string | undefined) {
  return useQuery<ExperienceRow | null>({
    queryKey: ['experiences', 'bySlug', slug],
    queryFn: async () => {
      if (!slug) return null;
      const { data, error } = await supabase
        .from('experiences')
        .select('slug, title, intro, hero_media_asset_id, match_rules, status, order_index')
        .eq('slug', slug)
        .eq('status', 'public')
        .maybeSingle();
      if (error) throw error;
      return data as ExperienceRow | null;
    },
    enabled: !!slug,
    staleTime: 5 * 60 * 1000,
  });
}
